/**
 * EventBindingsPage — Tasks & Templates · Event Bindings.
 *
 * The Wave-8 notifications spine routing table. Each row maps a platform
 * event code (e.g. 'cart-processed', 'signing-bounced', 'sla-breach') to:
 *   - which Notification Template fires
 *   - which channels are enabled (comma-separated subset of
 *     email / teams-dm / in-app / sms / teams-card)
 *   - which recipient resolver to use
 *   - quiet-hours policy
 *   - priority + retention
 *
 * Contract derived from primary sources (2026-05-18):
 *   - Read:    ConfigFunction.cs `/v3/config/notification-event-bindings`
 *   - Admin:   AdminNotificationEventBindingsFunction.cs (GET / POST upsert /
 *                DELETE soft-delete IsActive=false)
 *   - DTO:     NotifEventBindingUpsert (shared-types) — already correctly
 *                shaped to match the entity (no `Notes` field; notes are
 *                read-only / server-managed)
 *   - Live probe 2026-05-18 against /v3/config/notification-event-bindings
 *     confirmed 44 rows + the row shape matches the entity exactly
 *
 * NOT a port. Authored from first principles using admin-panel primitives
 * and MSAL Path B auth.
 *
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (no bypass-key; bypass-key gated OFF in prod).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
} from '@retail-ops/api-client';
import type { NotifEventBindingUpsert } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, NumberField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface EventBindingRow {
  eventCode: string;
  defaultTemplateCode: string;
  enabledChannels: string;
  defaultRecipientResolver: string;
  quietHours: string | null;
  priority: 'high' | 'normal' | 'low';
  retentionDays: number | null;
  isActive: boolean;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

interface EditDraft {
  isNew: boolean;
  eventCode: string;
  defaultTemplateCode: string;
  enabledChannels: string;
  defaultRecipientResolver: string;
  quietHours: string;
  priority: 'high' | 'normal' | 'low';
  retentionDays: number;
  isActive: boolean;
  lockedKey: boolean;
}

// Channel set is small + enumerable; observed in production rows.
// Don't constrain to this list server-side (the entity column is free-form
// nvarchar), but UI hints the canonical set so authors don't typo.
const CHANNEL_CODES = ['email', 'teams-dm', 'in-app', 'sms', 'teams-card'] as const;

// Recipient resolver values observed live (44 rows). Free-form server-side
// but enumerable in practice.
const RESOLVER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'customer-only', label: 'customer-only' },
  { value: 'originating-officer', label: 'originating-officer' },
  { value: 'cart-customer-and-officer', label: 'cart-customer-and-officer' },
  { value: 'compliance-team', label: 'compliance-team' },
  { value: 'system-ops', label: 'system-ops' },
  { value: 'lobby-supervisor', label: 'lobby-supervisor' },
];

const PRIORITY_OPTIONS: ReadonlyArray<{ value: 'high' | 'normal' | 'low'; label: string }> = [
  { value: 'high', label: 'high' },
  { value: 'normal', label: 'normal' },
  { value: 'low', label: 'low' },
];

function emptyDraft(): EditDraft {
  return {
    isNew: true,
    eventCode: '',
    defaultTemplateCode: '',
    enabledChannels: 'in-app',
    defaultRecipientResolver: 'originating-officer',
    quietHours: 'business-hours',
    priority: 'normal',
    retentionDays: 2555,
    isActive: true,
    lockedKey: false,
  };
}

function rowToDraft(row: EventBindingRow): EditDraft {
  return {
    isNew: false,
    eventCode: row.eventCode,
    defaultTemplateCode: row.defaultTemplateCode,
    enabledChannels: row.enabledChannels,
    defaultRecipientResolver: row.defaultRecipientResolver,
    quietHours: row.quietHours ?? '',
    priority: row.priority,
    retentionDays: row.retentionDays ?? 2555,
    isActive: row.isActive,
    lockedKey: true,
  };
}

function draftToUpsertBody(d: EditDraft): NotifEventBindingUpsert {
  return {
    eventCode: d.eventCode.trim(),
    defaultTemplateCode: d.defaultTemplateCode.trim(),
    enabledChannels: d.enabledChannels.trim(),
    defaultRecipientResolver: d.defaultRecipientResolver.trim(),
    quietHours: d.quietHours.trim() || null,
    priority: d.priority,
    retentionDays: d.retentionDays,
    isActive: d.isActive,
  };
}

function priorityTone(p: 'high' | 'normal' | 'low'): 'err' | 'info' | 'muted' {
  return p === 'high' ? 'err' : p === 'normal' ? 'info' : 'muted';
}

function channelsAsChips(s: string): string[] {
  return s
    .split(',')
    .map((c) => c.trim())
    .filter((c) => c.length > 0);
}

export function EventBindingsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<EventBindingRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        // /v3/config/notification-event-bindings returns ApiResult<readonly EventBinding[]>.
        // Live wire confirmed 2026-05-18: { success, data: [...] }.
        const resp = (await client.retail.config.notificationEventBindings()) as unknown as {
          success: boolean;
          error?: string;
          data?: ReadonlyArray<EventBindingRow>;
        };
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          setRows(resp.data);
        } else {
          setLoadError(resp.error ?? 'Failed to load event bindings.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    const n = filter.trim().toLowerCase();
    if (!n) return rows;
    return rows.filter((r) => {
      const hay =
        `${r.eventCode} ${r.defaultTemplateCode} ${r.enabledChannels} ${r.defaultRecipientResolver} ${r.priority}`.toLowerCase();
      return hay.includes(n);
    });
  }, [rows, filter]);

  const columns: ReadonlyArray<DataTableColumn<EventBindingRow>> = useMemo(
    () => [
      {
        header: 'Event',
        cell: (r) => <code className="cell-mono--bold">{r.eventCode}</code>,
      },
      {
        header: 'Template',
        cell: (r) => <code className="cell-mono">{r.defaultTemplateCode}</code>,
      },
      {
        header: 'Channels',
        cell: (r) => (
          <span className="pill-stack">
            {channelsAsChips(r.enabledChannels).map((c) => (
              <Pill key={c} tone="info">{c}</Pill>
            ))}
          </span>
        ),
      },
      {
        header: 'Resolver',
        cell: (r) => <span className="cell-desc">{r.defaultRecipientResolver}</span>,
      },
      {
        header: 'Priority',
        cell: (r) => <Pill tone={priorityTone(r.priority)}>{r.priority}</Pill>,
      },
      {
        header: 'Status',
        cell: (r) =>
          r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
      },
      {
        header: '',
        cell: (r) => <Button onClick={() => setDraft(rowToDraft(r))}>Edit</Button>,
      },
    ],
    [],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.eventCode.trim()) {
      setSaveError('Event code is required.');
      return;
    }
    if (!draft.defaultTemplateCode.trim()) {
      setSaveError('Default template code is required.');
      return;
    }
    if (!draft.enabledChannels.trim()) {
      setSaveError('At least one channel is required.');
      return;
    }
    // Soft-validate channel codes against the observed set so authors
    // don't accidentally typo (e.g. "team-dm" → "teams-dm"). Server is
    // free-form so this is a UX guardrail, not a hard rule.
    const channels = channelsAsChips(draft.enabledChannels);
    const unknown = channels.filter((c) => !CHANNEL_CODES.includes(c as typeof CHANNEL_CODES[number]));
    if (unknown.length > 0) {
      setSaveError(
        `Unknown channel(s): ${unknown.join(', ')}. Allowed: ${CHANNEL_CODES.join(', ')}. Save anyway? Edit the field to remove the warning.`,
      );
      return;
    }
    if (!draft.defaultRecipientResolver.trim()) {
      setSaveError('Recipient resolver is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.notificationEventBindings.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToUpsertBody(draft),
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'admin upsert returned success=false');
        return;
      }
      setDraft(null);
      setToast({
        tone: 'info',
        text: draft.isNew
          ? `Created binding for "${draft.eventCode}".`
          : `Updated binding for "${draft.eventCode}".`,
      });
      refresh();
    } catch (e) {
      setSaveError(
        ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
      );
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  const handleDisable = useCallback(async () => {
    if (!draft || draft.isNew) return;
    const code = draft.eventCode.trim();
    if (!code) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.notificationEventBindings.remove(
        ADMIN_AUTH_TOKEN_SENTINEL,
        code,
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'soft-delete returned success=false');
        return;
      }
      setDraft(null);
      setToast({ tone: 'warn', text: `Disabled binding for "${code}". Event no longer dispatches.` });
      refresh();
    } catch (e) {
      setSaveError(
        ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
      );
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load event bindings: {loadError}{' '}
        <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading event bindings…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`Event Bindings — ${filtered?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField
                label=""
                value={filter}
                onChange={setFilter}
                placeholder="Filter by event, template, channel, resolver, or priority…"
              />
            </div>
            <Button variant="primary" onClick={() => setDraft(emptyDraft())}>
              + Add Binding
            </Button>
          </div>
        }
      >
        <DataTable<EventBindingRow>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => r.eventCode}
          emptyState={<p className="empty">No event bindings match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add Event Binding' : `Edit ${draft?.eventCode ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={620}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              {!draft.isNew && draft.isActive && (
                <Button onClick={() => void handleDisable()} disabled={saving}>
                  Disable
                </Button>
              )}
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <TextField
              label="Event code"
              value={draft.eventCode}
              onChange={(v) => setDraft({ ...draft, eventCode: v })}
              placeholder="e.g. cart-processed, signing-bounced, sla-breach"
              disabled={draft.lockedKey}
              hint={
                draft.lockedKey
                  ? 'Event code is permanent — disable and recreate to re-key.'
                  : 'Stable identifier. Use a kebab-case scope.action pattern.'
              }
            />

            <TextField
              label="Default template code"
              value={draft.defaultTemplateCode}
              onChange={(v) => setDraft({ ...draft, defaultTemplateCode: v })}
              placeholder="e.g. CART_WELCOME_CUSTOMER"
              hint="Notification template fired by default for this event. Must match a code in dbo.NotificationTemplates."
            />

            <TextField
              label="Enabled channels"
              value={draft.enabledChannels}
              onChange={(v) => setDraft({ ...draft, enabledChannels: v })}
              placeholder="email,teams-dm,in-app"
              hint={`Comma-separated. Allowed values: ${CHANNEL_CODES.join(', ')}.`}
            />

            <SelectField
              label="Recipient resolver"
              value={draft.defaultRecipientResolver}
              onChange={(v) => setDraft({ ...draft, defaultRecipientResolver: v })}
              options={RESOLVER_OPTIONS}
              hint="Who receives the notification. The dispatcher resolves this hint to actual addresses at fire time."
            />

            <TextField
              label="Quiet hours"
              value={draft.quietHours}
              onChange={(v) => setDraft({ ...draft, quietHours: v })}
              placeholder="business-hours · always · 07:00-21:00 CT"
              hint="When the dispatcher may fire this event. 'always' = anytime; 'business-hours' = 08:00–17:00 CT M–F; explicit ranges supported."
            />

            <SelectField
              label="Priority"
              value={draft.priority}
              onChange={(v) => setDraft({ ...draft, priority: v as 'high' | 'normal' | 'low' })}
              options={PRIORITY_OPTIONS}
              hint="Dispatch ordering. High-priority events bypass retry-queue backpressure."
            />

            <NumberField
              label="Retention days"
              value={draft.retentionDays}
              onChange={(v) => setDraft({ ...draft, retentionDays: v ?? 2555 })}
              min={1}
              hint="How long the dispatch + audit rows are kept before sweep. 2555 = ~7 years (banking default)."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="When inactive, the event fires no notifications. Row stays for audit + history."
            />
          </div>
        )}
      </ModalShell>
    </>
  );
}
